import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../lib/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Clock, User, ArrowLeft, ArrowRight, CheckCircle } from "@phosphor-icons/react";

const TRADE_TINTS = [
  { g1: "#11523B", g2: "#0d3f2d", accent: "#E26D29" },
  { g1: "#1E4D4D", g2: "#0f3535", accent: "#F5A365" },
  { g1: "#3B2C1E", g2: "#28190f", accent: "#E26D29" },
  { g1: "#2B3F1E", g2: "#182610", accent: "#F5A365" },
  { g1: "#3B1E33", g2: "#26102b", accent: "#E26D29" },
];
const tintFor = (str) => {
  const s = String(str || "");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return TRADE_TINTS[Math.abs(h) % TRADE_TINTS.length];
};

export default function BlogArticle() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/blog/${slug}`);
        setPost(data.post);
        setRelated(data.related || []);
        // SEO tags
        document.title = `${data.post.title} — MyHandyHub`;
        const desc = document.querySelector('meta[name="description"]');
        if (desc) desc.setAttribute("content", data.post.meta_description);
        // JSON-LD structured data
        const existing = document.getElementById("blog-jsonld");
        if (existing) existing.remove();
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.id = "blog-jsonld";
        script.textContent = JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": data.post.title,
          "description": data.post.meta_description,
          "author": { "@type": "Person", "name": "Adeel", "jobTitle": "Founder of MyHandyHub" },
          "publisher": { "@type": "Organization", "name": "MyHandyHub", "url": "https://myhandyhub.co.uk" },
          "datePublished": data.post.published_at,
          "dateModified": data.post.updated_at,
          "keywords": (data.post.keywords || []).join(", "),
          "mainEntityOfPage": `https://myhandyhub.co.uk/blog/${slug}`,
        });
        document.head.appendChild(script);
      } catch (e) {
        setNotFound(true);
      }
    })();
    return () => {
      const s = document.getElementById("blog-jsonld");
      if (s) s.remove();
    };
  }, [slug]);

  if (notFound) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-5 py-16 text-center">
          <h1 className="font-display font-extrabold text-3xl" style={{ color: "var(--brand-green)" }}>Article not found</h1>
          <p className="text-[var(--text-muted)] mt-2">This article may have been unpublished or moved.</p>
          <Link to="/blog" className="btn-primary mt-6 inline-flex" data-testid="blog-notfound-back">
            <ArrowLeft weight="bold" size={16} /> Back to blog
          </Link>
        </div>
      </Layout>
    );
  }
  if (!post) return <Layout><div className="max-w-2xl mx-auto px-5 py-16 text-center text-[var(--text-muted)]">Loading article…</div></Layout>;

  const t = tintFor(post.trade + post.city);
  const publishedDate = post.published_at ? new Date(post.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : null;

  return (
    <Layout>
      {/* Hero cover */}
      <div
        className="relative overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${t.g1} 0%, ${t.g2} 100%)` }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: `radial-gradient(circle at 90% 10%, ${t.accent}33, transparent 55%), radial-gradient(circle at 10% 100%, ${t.accent}22, transparent 55%)` }}
        />
        <div className="relative max-w-3xl mx-auto px-5 py-14 text-white">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/70 hover:text-white transition" data-testid="blog-back">
            <ArrowLeft size={14} weight="bold" /> All articles
          </Link>
          <div className="mt-5 text-xs font-bold uppercase tracking-widest" style={{ color: t.accent }}>{post.trade} · {post.city}</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl mt-3 leading-[0.95]" data-testid="article-title">{post.title}</h1>
          <p className="text-white/85 mt-4 text-lg max-w-2xl">{post.excerpt}</p>
          <div className="mt-6 flex items-center gap-4 text-sm text-white/80 flex-wrap">
            <span className="inline-flex items-center gap-1.5"><User size={14} /> {post.author}</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1.5"><Clock size={14} /> {post.read_minutes} min read</span>
            {publishedDate && (
              <>
                <span>·</span>
                <span>{publishedDate}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Article body */}
      <div className="max-w-3xl mx-auto px-5 py-10">
        <article className="prose-blog" data-testid="article-body">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.body_markdown}</ReactMarkdown>
        </article>

        {/* Waitlist CTA */}
        <div
          className="mt-10 rounded-2xl p-6 sm:p-8 text-white relative overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${t.g1} 0%, ${t.g2} 100%)` }}
          data-testid="article-cta"
        >
          <div
            className="absolute -top-16 -right-16 w-56 h-56 rounded-full pointer-events-none"
            style={{ background: `radial-gradient(circle, ${t.accent}44, transparent 60%)` }}
          />
          <div className="relative">
            <div className="text-xs font-bold uppercase tracking-widest" style={{ color: t.accent }}>Launching soon</div>
            <h3 className="font-display font-black text-2xl sm:text-3xl mt-2">Get fair quotes from verified {post.trade.toLowerCase()}s in {post.city}.</h3>
            <p className="text-white/80 mt-2">Join our waitlist — the first 100 homeowners to post a job get a <strong style={{ color: t.accent }}>£20 Amazon voucher</strong>. Free to join, no card needed, takes 30 seconds.</p>
            <ul className="mt-4 space-y-1.5 text-sm text-white/85">
              <li className="flex items-center gap-2"><CheckCircle weight="fill" style={{ color: t.accent }} size={16} /> No lead fees, no commission</li>
              <li className="flex items-center gap-2"><CheckCircle weight="fill" style={{ color: t.accent }} size={16} /> Verified UK-only trades</li>
              <li className="flex items-center gap-2"><CheckCircle weight="fill" style={{ color: t.accent }} size={16} /> GDPR compliant · no cold calls</li>
            </ul>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-base"
              style={{ background: "#fff", color: t.g1 }}
              data-testid="article-cta-join"
            >
              Join the waitlist <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-12">
            <h3 className="font-display font-extrabold text-xl mb-4" style={{ color: "var(--brand-green)" }}>Keep reading</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {related.map((r) => (
                <Link key={r.slug} to={`/blog/${r.slug}`} className="brand-card p-4 hover:shadow-lg transition" data-testid={`related-${r.slug}`}>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--brand-orange)]">{r.trade} · {r.city}</div>
                  <h4 className="font-display font-bold text-base mt-1 line-clamp-2" style={{ color: "var(--brand-green)" }}>{r.title}</h4>
                  <div className="flex items-center gap-1 text-xs text-[var(--text-muted)] mt-2"><Clock size={12} /> {r.read_minutes} min read</div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Inline styles for the article prose */}
      <style>{`
        .prose-blog { font-size: 17px; line-height: 1.75; color: #1A2E25; }
        .prose-blog p { margin: 1em 0; }
        .prose-blog h2 { font-family: 'Outfit', system-ui, sans-serif; font-weight: 900; font-size: 1.55em; color: var(--brand-green); margin: 2em 0 0.5em; letter-spacing: -0.2px; }
        .prose-blog h3 { font-family: 'Outfit', system-ui, sans-serif; font-weight: 800; font-size: 1.2em; color: var(--brand-green); margin: 1.5em 0 0.4em; }
        .prose-blog strong { color: var(--brand-green); }
        .prose-blog ul, .prose-blog ol { margin: 1em 0 1em 1.4em; }
        .prose-blog li { margin: 0.35em 0; }
        .prose-blog table { width: 100%; border-collapse: collapse; margin: 1.2em 0; font-size: 0.95em; }
        .prose-blog th { background: var(--sand-warm, #F4EFE3); color: var(--brand-green); text-align: left; padding: 10px 12px; border-bottom: 2px solid var(--brand-orange); font-weight: 700; }
        .prose-blog td { padding: 9px 12px; border-bottom: 1px solid var(--border, #E4E2DC); }
        .prose-blog tr:last-child td { border-bottom: 0; }
        .prose-blog blockquote { border-left: 3px solid var(--brand-orange); padding-left: 1em; color: #4a5a52; font-style: italic; margin: 1.2em 0; }
        .prose-blog a { color: var(--brand-orange); text-decoration: underline; }
      `}</style>
    </Layout>
  );
}
